import * as React from 'react';

import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { useServerLearnerPaging } from 'bundles/enterprise-admin-messaging-v2/hooks/useServerLearnerPaging';

type Params = {
  /** Absent for the non-filter (manual) flow — the query is then skipped. */
  audience?: LearnerListAudience;
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  /** Total rows matched by the current query (across all pages). */
  filteredCount: number;
  loading: boolean;
  error: boolean;
};

/**
 * Server-backed counterpart to {@link useLearnerList}: read-only view of the
 * cohort. Exposes the same shape as the client-side hook so the learner-list
 * screen can swap data sources without changing its render. Every row is
 * `inList: true` (the viewer renders the cohort it's given).
 */
export const useAudienceLearnerList = ({ audience }: Params): Result => {
  const { searchQuery, setSearchQuery, page, setPage, pageCount, learners, total, loading, error } =
    useServerLearnerPaging({ audience });

  const pageRows: LearnerRow[] = React.useMemo(
    () => learners.map((learner) => ({ learner, inList: true })),
    [learners]
  );

  return { searchQuery, setSearchQuery, page, setPage, pageCount, pageRows, filteredCount: total, loading, error };
};

export default useAudienceLearnerList;
